/**
 * Canonical order status model for the admin dashboard.
 *
 * Single source of truth on the client side. Mirrors:
 *  - backend enum: backend/db/migrations/095_canonical_order_status_check.js
 *    and backend/src/services/order-state-machine.js
 *  - customer-visible mapping: customer-app/src/components/order/OrderTimeline.tsx
 *    (orderStatusToStep) so admins see the SAME step the customer sees.
 *
 * No other file should define its own status string list, label, or color.
 */

export const ORDER_STATUSES = [
  'placed',
  'confirmed',
  'searching_agent',
  'agent_assigned',
  'en_route_pickup',
  'arrived_pickup',
  'picked_up',
  'at_facility',
  'in_wash',
  'in_iron',
  'quality_check',
  'ready',
  'out_for_delivery',
  'arrived_dropoff',
  'delivered',
  'cancelled',
  'refunded',
  'reschedule_pickup_pending',
  'reschedule_delivery_pending',
  'no_show',
  'returned',
  'failed',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

// Customer-visible timeline steps (must match customer-app STEP_KEYS/labels).
export type CustomerStep =
  | 'placed'
  | 'searching_agent'
  | 'agent_assigned'
  | 'picked_up'
  | 'at_facility'
  | 'in_progress'
  | 'ready'
  | 'out_for_delivery'
  | 'delivered'

export const CUSTOMER_STEP_LABELS: Record<CustomerStep, string> = {
  placed: 'Order placed',
  searching_agent: 'Finding pickup partner',
  agent_assigned: 'Agent assigned',
  picked_up: 'Picked up',
  at_facility: 'Received at hub',
  in_progress: 'Processing',
  ready: 'Ready for dispatch',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
}

/**
 * Map any canonical status to the customer-visible step (or null for terminal
 * states that aren't on the timeline). Kept byte-for-byte consistent with the
 * customer app's orderStatusToStep.
 */
export function orderStatusToCustomerStep(status: string): CustomerStep | null {
  switch (status) {
    case 'placed':
    case 'confirmed':
      return 'placed'
    case 'searching_agent':
      return 'searching_agent'
    case 'agent_assigned':
    case 'en_route_pickup':
    case 'arrived_pickup':
      return 'agent_assigned'
    case 'picked_up':
      return 'picked_up'
    case 'at_facility':
      return 'at_facility'
    case 'in_wash':
    case 'in_iron':
    case 'quality_check':
      return 'in_progress'
    case 'ready':
      return 'ready'
    case 'out_for_delivery':
    case 'arrived_dropoff':
      return 'out_for_delivery'
    case 'delivered':
      return 'delivered'
    case 'cancelled':
    case 'refunded':
    case 'returned':
    case 'failed':
      return null
    default:
      return 'placed'
  }
}

/** What the customer currently sees for this status (e.g. "Received at hub"). */
export function customerFacingLabel(status: string): string {
  const step = orderStatusToCustomerStep(status)
  if (!step) return adminStatusLabel(status)
  return CUSTOMER_STEP_LABELS[step]
}

type StatusMeta = { label: string; color: string; group: 'pre' | 'pickup' | 'facility' | 'delivery' | 'terminal' | 'exception' }

// Operational label + badge color for every canonical status.
export const STATUS_META: Record<OrderStatus, StatusMeta> = {
  placed: { label: 'Placed', color: '#6B7280', group: 'pre' },
  confirmed: { label: 'Confirmed', color: '#6B7280', group: 'pre' },
  searching_agent: { label: 'Finding agent', color: '#F59E0B', group: 'pre' },
  agent_assigned: { label: 'Agent assigned', color: '#3B82F6', group: 'pickup' },
  en_route_pickup: { label: 'En route (pickup)', color: '#3B82F6', group: 'pickup' },
  arrived_pickup: { label: 'Arrived (pickup)', color: '#3B82F6', group: 'pickup' },
  picked_up: { label: 'Picked up', color: '#6366F1', group: 'pickup' },
  at_facility: { label: 'At facility', color: '#8B5CF6', group: 'facility' },
  in_wash: { label: 'In wash', color: '#8B5CF6', group: 'facility' },
  in_iron: { label: 'In iron', color: '#8B5CF6', group: 'facility' },
  quality_check: { label: 'Quality check', color: '#8B5CF6', group: 'facility' },
  ready: { label: 'Ready', color: '#0EA5E9', group: 'facility' },
  out_for_delivery: { label: 'Out for delivery', color: '#0EA5E9', group: 'delivery' },
  arrived_dropoff: { label: 'Arrived (dropoff)', color: '#0EA5E9', group: 'delivery' },
  delivered: { label: 'Delivered', color: '#10B981', group: 'terminal' },
  cancelled: { label: 'Cancelled', color: '#EF4444', group: 'terminal' },
  refunded: { label: 'Refunded', color: '#EF4444', group: 'terminal' },
  reschedule_pickup_pending: { label: 'Reschedule (pickup)', color: '#F59E0B', group: 'exception' },
  reschedule_delivery_pending: { label: 'Reschedule (delivery)', color: '#F59E0B', group: 'exception' },
  no_show: { label: 'No show', color: '#EF4444', group: 'exception' },
  returned: { label: 'Returned', color: '#EF4444', group: 'exception' },
  failed: { label: 'Failed', color: '#EF4444', group: 'exception' },
}

export function adminStatusLabel(status: string): string {
  return STATUS_META[status as OrderStatus]?.label ?? status
}

export function adminStatusColor(status: string): string {
  return STATUS_META[status as OrderStatus]?.color ?? '#6B7280'
}

// Lifecycle state sets used for admin action gating (state legality only;
// RBAC is applied separately via useCan).
export const CANCEL_ALLOWED_STATES = new Set<string>([
  'placed',
  'confirmed',
  'searching_agent',
  'agent_assigned',
  'en_route_pickup',
  'arrived_pickup',
])

export const POST_PICKUP_NON_TERMINAL_STATES = new Set<string>([
  'picked_up',
  'at_facility',
  'in_wash',
  'in_iron',
  'quality_check',
  'ready',
  'out_for_delivery',
  'arrived_dropoff',
  'delivered',
])

export const TERMINAL_STATES = new Set<string>([
  'delivered',
  'cancelled',
  'refunded',
  'returned',
  'failed',
])

export const NON_TERMINAL_STATES = new Set<string>(
  ORDER_STATUSES.filter((s) => !TERMINAL_STATES.has(s)),
)
